<template>
  <BasicTemplate>
    <v-card>

      <v-card-title class="d-flex align-center">
        <h3 class="d-flex align-center mb-0" data-testid="games-page-title">
          <v-icon class="mr-2">mdi-controller-classic</v-icon>
          Games
        </h3>
        <v-spacer />
        <span class="mr-2">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="info" v-bind="attrs" v-on="on">
                <v-icon left>mdi-file-excel</v-icon>
                Export to Excel
                <v-icon right>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="exportToExcel('filtered')">
                <v-list-item-title>
                  Export Filtered Games ({{ visibleItems.length }})
                </v-list-item-title>
              </v-list-item>
              <v-list-item @click="exportToExcel('all')">
                <v-list-item-title>
                  Export All Games ({{ items.length }})
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </span>
        <v-btn
          class="mr-2"
          color="info"
          :loading="loading"
          data-testid="games-refresh-btn"
          @click="load"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn
          color="success"
          data-testid="games-create-btn"
          @click="$router.push('/games/new')"
        >
          Create
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-text-field
          label="Search"
          v-model="search"
          prepend-icon="mdi-magnify"
          clearable
          dense
          hide-details
          class="mb-4"
        />
        
        <!-- Filters -->
        <v-expansion-panels class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <v-icon left>mdi-filter</v-icon>
              Filters
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <!-- Identity -->
              <v-card class="mb-4 games-filters__section" elevation="1">
                <v-card-title class="games-filters__section-title">Identity</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="filters.slug"
                        label="Slug"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="filters.name"
                        label="Name"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.tier"
                        :items="tierOptions"
                        label="Tier"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-autocomplete
                        v-model="filters.developerId"
                        :items="developerOptions"
                        label="Developer"
                        clearable
                        dense
                        hide-no-data
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-autocomplete
                        v-model="filters.metaTemplateId"
                        :items="metaTemplateOptions"
                        label="Meta Template"
                        clearable
                        dense
                        hide-no-data
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Tech stack -->
              <v-card class="mb-4 games-filters__section" elevation="1">
                <v-card-title class="games-filters__section-title">Tech stack</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.renderingType"
                        :items="renderingTypeOptions"
                        label="Rendering Type"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="filters.sdk"
                        label="SDK"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.engine"
                        :items="engineOptions"
                        label="Engine"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="filters.engineVersion"
                        label="Engine Version"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="filters.sdkName"
                        label="Storage Path"
                        clearable
                        dense
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Platform / auth / session -->
              <v-card class="mb-4 games-filters__section" elevation="1">
                <v-card-title class="games-filters__section-title">Platform / auth / session</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasAuth"
                        :items="booleanOptions"
                        label="Auth"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasEagle"
                        :items="booleanOptions"
                        label="Eagle"
                        clearable
                        dense
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Progress & persistence -->
              <v-card class="mb-4 games-filters__section" elevation="1">
                <v-card-title class="games-filters__section-title">Progress &amp; persistence</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasInventory"
                        :items="booleanOptions"
                        label="Inventory"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasCollection"
                        :items="booleanOptions"
                        label="Collection"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasLeaderboard"
                        :items="booleanOptions"
                        label="Leaderboard"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasMultiplayer"
                        :items="booleanOptions"
                        label="Multiplayer"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasPlatformGameEnd"
                        :items="booleanOptions"
                        label="Platform Game End"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasCloudSave"
                        :items="booleanOptions"
                        label="Cloud Save"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="filters.cloudSaveStorage"
                        label="Cloud Save Storage"
                        clearable
                        dense
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Monetization -->
              <v-card class="mb-4 games-filters__section" elevation="1">
                <v-card-title class="games-filters__section-title">Monetization (MTX / ads)</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasGems"
                        :items="booleanOptions"
                        label="Gems"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasInterstitialAds"
                        :items="booleanOptions"
                        label="Interstitial Ads"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasRewardedAds"
                        :items="booleanOptions"
                        label="Rewarded Ads"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.isAdsFree"
                        :items="booleanOptions"
                        label="Ads Free"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.adPodding"
                        :items="booleanOptions"
                        label="Ad Podding"
                        clearable
                        dense
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Rendering & layout -->
              <v-card class="mb-4 games-filters__section" elevation="1">
                <v-card-title class="games-filters__section-title">Rendering &amp; layout</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.isGRM"
                        :items="booleanOptions"
                        label="GRM"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.isIframe"
                        :items="booleanOptions"
                        label="Iframe"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.isFlex"
                        :items="booleanOptions"
                        label="Flexible Size"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasFullscreen"
                        :items="booleanOptions"
                        label="Full Screen"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasPortraitMode"
                        :items="booleanOptions"
                        label="Portrait Mode"
                        clearable
                        dense
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Availability -->
              <v-card class="mb-4 games-filters__section" elevation="1">
                <v-card-title class="games-filters__section-title">Availability</v-card-title>
                <v-card-text>
                  <v-row dense>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.hasComingSoon"
                        :items="booleanOptions"
                        label="Coming Soon"
                        clearable
                        dense
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-select
                        v-model="filters.isSubscriberOnly"
                        :items="booleanOptions"
                        label="Subscriber Only"
                        clearable
                        dense
                      />
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <v-row>
                <v-col cols="12">
                  <v-btn
                    color="error"
                    @click="clearFilters"
                    small
                  >
                    Clear All Filters
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Results Counter -->
        <div class="mb-4">
          <v-chip color="info" small>
            Showing {{ visibleItems.length }} of {{ items.length }} games
          </v-chip>
        </div>

        <v-data-table 
          :options.sync="tableOptions"
          :loading="loading" 
          :headers="headers" 
          :items="filteredItems" 
          :search="search"
          class="games-table"
        >
          <template v-slot:item.isGRM="{ item }">
            <v-icon :color="item.isGRM ? 'primary' : 'grey'" :disabled="true">
              {{ item.isGRM ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
          </template>
          <template v-slot:item.isIframe="{ item }">
            <v-icon :color="item.isIframe ? 'primary' : 'grey'" :disabled="true">
              {{ item.isIframe ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
          </template>
          <template v-slot:item.isAdsFree="{ item }">
            <v-icon :color="item.isAdsFree ? 'primary' : 'grey'" :disabled="true">
              {{ item.isAdsFree ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
          </template>
          <template v-slot:item.hasEagle="{ item }">
            <v-icon :color="item.hasEagle ? 'primary' : 'grey'" :disabled="true">
              {{ item.hasEagle ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
          </template>
          <template v-slot:item.hasGems="{ item }">
            <v-icon :color="item.hasGems ? 'primary' : 'grey'" :disabled="true">
              {{ item.hasGems ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
          </template>
          <template v-slot:item.hasFullscreen="{ item }">
            <v-icon :color="item.hasFullscreen ? 'primary' : 'grey'" :disabled="true">
              {{ item.hasFullscreen ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
          </template>
          <template v-slot:item.hasLeaderboard="{ item }">
            <v-icon :color="item.hasLeaderboard ? 'primary' : 'grey'" :disabled="true">
              {{ item.hasLeaderboard ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
            </v-icon>
          </template>
          <template v-slot:item.metaTemplate="{ item }">
            {{ metaTemplateLabel(item) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="games-list__actions">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    :data-testid="'games-edit-' + item.id"
                    @click="editItem(item)"
                  >
                    mdi-pencil
                  </v-icon>
                </template>
                <span>Edit</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="secondary"
                    tag="a"
                    :href="changeLogHref(item)"
                    target="_blank"
                    rel="noopener noreferrer"
                    :data-testid="'games-history-' + item.id"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-history
                  </v-icon>
                </template>
                <span>View change history</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="warning"
                    v-bind="attrs"
                    v-on="on"
                    :data-testid="'games-delete-' + item.id"
                    @click="deleteItem(item)"
                  >
                    mdi-delete
                  </v-icon>
                </template>
                <span>Delete</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

    </v-card>
  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';
import { exportGamesToExcel } from '../../utils/gameExcelExport';

export default {
  name: 'GamesList',
  components: {BasicTemplate},
  data() {
    return {
      search: '',
      loading: false,
      items: [],
      metaTemplates: [],
      developers: [],
      tableOptions: {
        sortBy: ['id'],
        sortDesc: [false],
      },
      headers: [
        {text: 'Id', value: 'id'},
        {text: 'Slug', value: 'slug'},
        {text: 'Name', value: 'name'},
        {text: 'GRM', value: 'isGRM'},
        {text: 'Iframe', value: 'isIframe'},
        {text: 'Ads Free', value: 'isAdsFree'},
        {text: 'Has Eagle', value: 'hasEagle'},
        {text: 'Has Gems', value: 'hasGems'},
        {text: 'Full Screen', value: 'hasFullscreen'},
        {text: 'Has LB', value: 'hasLeaderboard'},
        {text: 'Meta', value: 'metaTemplate'},
        {text: 'Actions', value: 'actions', sortable: false, align: 'center'},
      ],
      filters: {
        name: '',
        slug: '',
        renderingType: '',
        sdk: '',
        engine: '',
        engineVersion: '',
        sdkName: '',
        isGRM: '',
        isIframe: '',
        isAdsFree: '',
        hasEagle: '',
        hasGems: '',
        hasFullscreen: '',
        hasLeaderboard: '',
        hasComingSoon: '',
        isSubscriberOnly: '',
        metaTemplateId: '',
        tier: '',
        developerId: '',
        isFlex: '',
        hasPortraitMode: '',
        hasAuth: '',
        hasInventory: '',
        hasCollection: '',
        hasMultiplayer: '',
        hasPlatformGameEnd: '',
        hasCloudSave: '',
        cloudSaveStorage: '',
        hasInterstitialAds: '',
        hasRewardedAds: '',
        adPodding: '',
      },
      renderingTypeOptions: ['Html', 'JavaScript', 'External'],
      tierOptions: ['S', 'A', 'B', 'C', 'D'],
      engineOptions: ['HTML', 'Core', 'Unity', 'Construct', 'Other', 'Defold', 'Godot'].sort(),
      booleanOptions: ['', 'true', 'false'],
    };
  },
  computed: {
    metaTemplateOptions() {
      const templates = (this.metaTemplates || [])
        .filter(template => template?.id != null)
        .map(template => ({
          text: template.name || `#${template.id}`,
          value: String(template.id),
        }))
        .sort((a, b) => a.text.localeCompare(b.text));
      
      // Add "Not Set" option for null values at the beginning
      return [
        { text: 'Not Set', value: '__null__' },
        ...templates
      ];
    },
    developerOptions() {
      const developers = (this.developers || [])
        .filter(developer => developer?.id != null)
        .map(developer => ({
          text: developer.name || `#${developer.id}`,
          value: String(developer.id),
        }))
        .sort((a, b) => a.text.localeCompare(b.text));

      return [
        { text: 'Not Set', value: '__null__' },
        ...developers
      ];
    },
    metaTemplateMap() {
      return (this.metaTemplates || []).reduce((acc, template) => {
        if (template?.id != null) {
          acc[String(template.id)] = template.name || `#${template.id}`;
        }
        return acc;
      }, {});
    },
    filteredItems() {
      return this.items.filter(item => {
        return (
          (!this.filters.name || item.name.toLowerCase().includes(this.filters.name.toLowerCase())) &&
          (!this.filters.slug || item.slug.toLowerCase().includes(this.filters.slug.toLowerCase())) &&
          (!this.filters.renderingType || item.renderingType === this.filters.renderingType) &&
          (!this.filters.sdk || (item.sdk || '').toLowerCase().includes(this.filters.sdk.toLowerCase())) &&
          (!this.filters.engine || item.engine === this.filters.engine) &&
          (!this.filters.engineVersion || (item.engineVersion || '').toLowerCase().includes(this.filters.engineVersion.toLowerCase())) &&
          (!this.filters.sdkName || (item.sdkName || '').toLowerCase().includes(this.filters.sdkName.toLowerCase())) &&
          (this.filters.isGRM === '' || item.isGRM === (this.filters.isGRM === 'true')) &&
          (this.filters.isIframe === '' || item.isIframe === (this.filters.isIframe === 'true')) &&
          (this.filters.isAdsFree === '' || item.isAdsFree === (this.filters.isAdsFree === 'true')) &&
          (this.filters.hasEagle === '' || item.hasEagle === (this.filters.hasEagle === 'true')) &&
          (this.filters.hasGems === '' || item.hasGems === (this.filters.hasGems === 'true')) &&
          (this.filters.hasFullscreen === '' || item.hasFullscreen === (this.filters.hasFullscreen === 'true')) &&
          (this.filters.hasLeaderboard === '' || item.hasLeaderboard === (this.filters.hasLeaderboard === 'true')) &&
          (this.filters.hasComingSoon === '' || item.hasComingSoon === (this.filters.hasComingSoon === 'true')) &&
          (this.filters.isSubscriberOnly === '' || item.isSubscriberOnly === (this.filters.isSubscriberOnly === 'true')) &&
          (this.filters.metaTemplateId === '' ||
            (this.filters.metaTemplateId === '__null__'
              ? (item.metaTemplateId == null && item.metaTemplate?.id == null)
              : String(item.metaTemplateId ?? item.metaTemplate?.id ?? '') === this.filters.metaTemplateId)) &&
          (!this.filters.tier || item.tier === this.filters.tier) &&
          (this.filters.developerId === '' ||
            (this.filters.developerId === '__null__'
              ? (item.developerId == null && item.developer?.id == null)
              : String(item.developerId ?? item.developer?.id ?? '') === this.filters.developerId)) &&
          (this.filters.isFlex === '' || item.isFlex === (this.filters.isFlex === 'true')) &&
          (this.filters.hasPortraitMode === '' || item.hasPortraitMode === (this.filters.hasPortraitMode === 'true')) &&
          (this.filters.hasAuth === '' || item.hasAuth === (this.filters.hasAuth === 'true')) &&
          (this.filters.hasInventory === '' || item.hasInventory === (this.filters.hasInventory === 'true')) &&
          (this.filters.hasCollection === '' || item.hasCollection === (this.filters.hasCollection === 'true')) &&
          (this.filters.hasMultiplayer === '' || item.hasMultiplayer === (this.filters.hasMultiplayer === 'true')) &&
          (this.filters.hasPlatformGameEnd === '' || item.hasPlatformGameEnd === (this.filters.hasPlatformGameEnd === 'true')) &&
          (this.filters.hasCloudSave === '' || item.hasCloudSave === (this.filters.hasCloudSave === 'true')) &&
          (!this.filters.cloudSaveStorage || (item.cloudSaveStorage || '').toLowerCase().includes(this.filters.cloudSaveStorage.toLowerCase())) &&
          (this.filters.hasInterstitialAds === '' || item.hasInterstitialAds === (this.filters.hasInterstitialAds === 'true')) &&
          (this.filters.hasRewardedAds === '' || item.hasRewardedAds === (this.filters.hasRewardedAds === 'true')) &&
          (this.filters.adPodding === '' || item.adPodding === (this.filters.adPodding === 'true'))
        );
      });
    },
    visibleItems() {
      // Filters + Search (same set the grid shows)
      const query = (this.search || '').trim().toLowerCase();
      if (!query) return this.filteredItems;

      return this.filteredItems.filter((item) => this.itemMatchesGlobalSearch(item, query));
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    changeLogLink(item) {
      return {
        path: '/game-data-change-log',
        query: {
          table: 'game',
          entityId: String(item.id),
        },
      };
    },
    changeLogHref(item) {
      return this.$router.resolve(this.changeLogLink(item)).href;
    },
    async load() {
      this.loading = true;
      try {
        await this.$msal.ensureAuthenticated();
        const [games, metaTemplates, developers] = await Promise.all([
          this.$gameApi.get('/games'),
          this.$gameApi.get('/meta-templates'),
          this.$gameApi.get('/users'),
        ]);
        this.items = games || [];
        this.metaTemplates = metaTemplates || [];
        this.developers = developers || [];
      } catch (error) {
        console.error('Error loading games:', error);
        this.items = [];
        this.metaTemplates = [];
        this.developers = [];
      } finally {
        this.loading = false;
      }
    },
    metaTemplateLabel(item) {
      if (!item) return '';
      const id = item.metaTemplateId ?? item.metaTemplate?.id;
      if (id == null) return '';
      return this.metaTemplateMap[String(id)] || `#${id}`;
    },
    itemMatchesGlobalSearch(item, query) {
      if (!query) return true;
      // Mirror v-data-table search over visible columns (+ meta label)
      for (const header of this.headers) {
        if (!header?.value || header.value === 'actions') continue;
        let value;
        if (header.value === 'metaTemplate') {
          value = this.metaTemplateLabel(item);
        } else {
          value = item?.[header.value];
        }
        if (value == null || typeof value === 'boolean') continue;
        if (String(value).toLowerCase().includes(query)) return true;
      }
      return false;
    },
    async editItem(item) {
      this.$router.push(`/games/${item.id}`);
    },
    async deleteItem(item) {
      const {isConfirmed} = await this.$swal.fire({
        icon: 'warning',
        title: 'This will delete all feed infromation. Are you sure to proceed?',
        showCancelButton: true
      });
      if (isConfirmed) {
        try {
          // Ensure authentication before deleting
          await this.$msal.ensureAuthenticated();
          await this.$gameApi.delete(`/games/${item.id}`);
          this.items = await this.$gameApi.get('/games/');
        } catch (error) {
          console.error('Error deleting game:', error);
        }
      }
    },
    clearFilters() {
      this.filters = {
        name: '',
        slug: '',
        renderingType: '',
        sdk: '',
        engine: '',
        engineVersion: '',
        sdkName: '',
        isGRM: '',
        isIframe: '',
        isAdsFree: '',
        hasEagle: '',
        hasGems: '',
        hasFullscreen: '',
        hasLeaderboard: '',
        hasComingSoon: '',
        isSubscriberOnly: '',
        metaTemplateId: '',
        tier: '',
        developerId: '',
        isFlex: '',
        hasPortraitMode: '',
        hasAuth: '',
        hasInventory: '',
        hasCollection: '',
        hasMultiplayer: '',
        hasPlatformGameEnd: '',
        hasCloudSave: '',
        cloudSaveStorage: '',
        hasInterstitialAds: '',
        hasRewardedAds: '',
        adPodding: '',
      };
    },
    sortValueForExport(item, key) {
      if (key === 'metaTemplate') {
        return this.metaTemplateLabel(item);
      }
      return item?.[key];
    },
    compareExportValues(a, b) {
      if (a == null && b == null) return 0;
      if (a == null) return -1;
      if (b == null) return 1;
      if (typeof a === 'number' && typeof b === 'number') return a - b;
      if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    },
    sortGamesLikeGrid(items) {
      const sortBy = this.tableOptions?.sortBy?.[0] || 'id';
      const sortDesc = !!(this.tableOptions?.sortDesc?.[0]);
      return [...items].sort((left, right) => {
        const cmp = this.compareExportValues(
          this.sortValueForExport(left, sortBy),
          this.sortValueForExport(right, sortBy)
        );
        return sortDesc ? -cmp : cmp;
      });
    },
    exportToExcel(type = 'filtered') {
      try {
        const source = type === 'filtered' ? this.visibleItems : this.items;
        const dataToExport = this.sortGamesLikeGrid(source);
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const filename = `games_export_${type}_${timestamp}.xlsx`;

        const { count } = exportGamesToExcel(dataToExport, {
          developers: this.developers,
          metaTemplates: this.metaTemplates,
          filename,
          sheetName: 'Games',
        });

        this.$swal.fire({
          icon: 'success',
          title: 'Export Successful',
          text: `Exported ${count} games to ${filename}`,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Export error:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Export Failed',
          text: error.message || 'An error occurred during export',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    },
  }
};
</script>

<style scoped>
::v-deep .games-table .v-data-table-header th {
  vertical-align: middle;
}

::v-deep .games-table .v-data-table-header th .v-data-table-header__text {
  white-space: nowrap;
  line-height: 1.2;
}

.games-filters__section {
  border-left: 4px solid #1976d2;
  overflow: hidden;
}

.games-filters__section-title {
  display: flex;
  align-items: center;
  padding-top: 12px !important;
  padding-bottom: 8px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
}

.games-filters__section ::v-deep > .v-card-text {
  padding-top: 4px !important;
  padding-bottom: 8px !important;
}

.games-list__actions {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
}
</style>

